import cn from "classnames"
import React, { useEffect, useState } from "react"
import { SubmitHandler, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { Form } from "reactstrap"
import {
    CleanHtml,
    PageContainer,
    PageWrapper,
    ShowErrorMessages,
    TitleWithFoldableHint,
    useSelector,
    valFuncRequired,
} from "swiipe.portal.shared"
import SubmitButton from "../../components/buttons/SubmitButton"
import FixedLabelInput from "../../components/form/input/FixedLabelInput"
import CancelService from "../../components/merchant/CancelService"
import GoBackContainer from "../../components/navigations/GoBackContainer"
import { SharedCancelContainer } from "../../components/servicesonboarding/SharedCancelContainer"
import { getImage } from "../../services/imageLanguageService"
import { StoreState } from "../../store/StoreState"
import { gatewaySelectors, IGatewayDto } from "../../store/reducers/gatewayReducer"
import { getOnboardingStatusThunk } from "../../store/thunks/swiipePaymentsThunks"
import { sendServiceCancellationEmailThunk } from "../../store/thunks/swiipeServicesThunks"
import {
    getVippsOnboardingDataThunk,
    IVippsOnboardingConnectData,
    vippsDisableOnboardingThunk,
    vippsOnboardingConnectThunk,
} from "../../store/thunks/vippsMobilePayThunks"
import { useReduxDispatch } from "../../store/useReduxDispatch"
import "./VippsOnboardingPage.scss"
import { IFormModel, IVippsOnboardingPageInnerProps } from "./VippsProps"

const VmpVippsOnboardingPageInner = ({
    merchantDetails,
    webshopIds,
    selectedWebshopId,
    isEdit,
    onGoBack,
    onOnboarded,
    guideContent,
}: IVippsOnboardingPageInnerProps) => {
    const { t } = useTranslation()
    const dispatch = useReduxDispatch()

    const [showCancelService, setShowCancelService] = useState(false)

    useEffect(() => {
        merchantDetails?.webshops.forEach(
            async (w) =>
                await dispatch(getOnboardingStatusThunk(merchantDetails?.swMerchant.swMerchantId, w.webshopId, false, ["Vipps"]))
        )
    }, [merchantDetails])

    useEffect(() => {
        if (isEdit && selectedWebshopId) {
            ;(async () => {
                const response = await dispatch(getVippsOnboardingDataThunk(selectedWebshopId))

                setValue("vippsId", response.msn)
            })()
        }
    }, [isEdit, selectedWebshopId])

    const gateway = useSelector<StoreState, IGatewayDto | undefined>((state) =>
        gatewaySelectors.getGateway(
            state,
            merchantDetails.swMerchant.swMerchantId,
            "Vipps",
            selectedWebshopId || "",
            "VippsMobilePay"
        )
    )

    const { handleSubmit, register, setValue, errors, formState } = useForm<IFormModel>()

    if (!webshopIds && !isEdit) {
        return null
    }

    const onSubmit: SubmitHandler<IFormModel> = async (data: IFormModel) => {
        try {
            const formData: IVippsOnboardingConnectData = {
                swMerchantId: merchantDetails.swMerchant.swMerchantId,
                webshopId: selectedWebshopId || "",
                msn: data.vippsId,
                clientId: data.vippsClientId,
                clientSecret: data.vippsClientSecret,
                subscriptionKey: data.vippsSubscriptionKey,
            }

            await dispatch(vippsOnboardingConnectThunk(formData))

            onOnboarded()
        } catch {
            //Catch to stop showing spinner
        }
    }

    return (
        <>
            {showCancelService && gateway && (
                <CancelService
                    serviceName={t("vippsonboarding.vipps")}
                    cancelText={t("cancelService.vipps.cancelText")}
                    customCancelledText={t("cancelService.vipps.cancelledText")}
                    onCancel={async () => {
                        await dispatch(
                            vippsDisableOnboardingThunk(
                                merchantDetails.swMerchant.swMerchantId,
                                selectedWebshopId || "",
                                gateway.gatewayId
                            )
                        )

                        await dispatch(
                            sendServiceCancellationEmailThunk(
                                merchantDetails.swMerchant.swMerchantId,
                                "GeneralServiceCancellationEopEmail",
                                t("serviceNames.vipps"),
                                true
                            )
                        )
                    }}
                    onGoBack={() => setShowCancelService(false)}
                />
            )}
            <PageWrapper className={cn({ "d-none": showCancelService })}>
                {isEdit && <GoBackContainer goBackText={t("commononboardingtexts.backToAllService")} onGoBack={onGoBack} />}
                <PageContainer noHeader={isEdit}>
                    <div className="vipps-onboarding-container">
                        <div className="vipps-form-head">
                            <img className="swiipe-payments" src={getImage("vippsLogo")} />
                        </div>

                        {!isEdit && <div className="vipps-text-1">{t("vippsonboarding.toactivate")}</div>}

                        {!isEdit && <CleanHtml element="div" className="vipps-text-2" html={t("vippsonboarding.donthaveone")} />}

                        <Form onSubmit={handleSubmit(onSubmit)} className="vipps-form">
                            <FixedLabelInput
                                label={t("vippsonboarding.vippsid")}
                                name="vippsId"
                                innerRef={register(valFuncRequired(t("vippsonboarding.vippsiderror")))}
                                placeholder=""
                                disabled={isEdit}
                            />

                            {!isEdit && (
                                <>
                                    <FixedLabelInput
                                        label={t("vippsonboarding.vippssubscriptionkey")}
                                        name="vippsSubscriptionKey"
                                        innerRef={register(valFuncRequired(t("vippsonboarding.vippsSubscriptionKeyError")))}
                                        placeholder=""
                                    />

                                    <FixedLabelInput
                                        label={t("vippsonboarding.vippsclientid")}
                                        name="vippsClientId"
                                        innerRef={register(valFuncRequired(t("vippsonboarding.vippsClientIdError")))}
                                        placeholder=""
                                    />

                                    <FixedLabelInput
                                        label={t("vippsonboarding.vippsclientsecret")}
                                        name="vippsClientSecret"
                                        innerRef={register(valFuncRequired(t("vippsonboarding.vippsClientSecretError")))}
                                        placeholder=""
                                    />

                                    <TitleWithFoldableHint
                                        title={t("vippsonboarding.whereToFindInfo")}
                                        hint={guideContent}
                                        theme="grey"
                                        className="mb-5"
                                    />

                                    <div className="vipps-onboarding-form mt-4">
                                        <ShowErrorMessages formState={formState} />

                                        <SubmitButton
                                            dark
                                            noBorder
                                            noBorderRadius
                                            containerClass="submit-btn"
                                            formState={formState}
                                        >
                                            {t("commononboardingtexts.submit")}
                                        </SubmitButton>
                                    </div>
                                </>
                            )}
                        </Form>

                        {!isEdit && (
                            <div className="vipps-back-link mt-3">
                                <a
                                    onClick={() => {
                                        onGoBack()
                                    }}
                                >
                                    {t("commononboardingtexts.back")}
                                </a>
                            </div>
                        )}
                        {isEdit && (
                            <SharedCancelContainer
                                cancelText={t("vippsonboarding.cancel")}
                                onCancel={() => setShowCancelService(true)}
                            />
                        )}
                    </div>
                </PageContainer>
            </PageWrapper>
        </>
    )
}

export default VmpVippsOnboardingPageInner
